/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * Change the aud claim format used in private_key_jwt assertions. Omit to leave unchanged.
 */
export const UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat = {
  Issuer: "issuer",
  TokenEndpoint: "token_endpoint",
} as const;
/**
 * Change the aud claim format used in private_key_jwt assertions. Omit to leave unchanged.
 */
export type UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat =
  ClosedEnum<
    typeof UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat
  >;

/**
 * Change how the client authenticates at the issuer's token endpoint.
 */
export const UpdateRemoteSessionClientFormTokenEndpointAuthMethod = {
  ClientSecretBasic: "client_secret_basic",
  ClientSecretPost: "client_secret_post",
  None: "none",
  PrivateKeyJwt: "private_key_jwt",
} as const;
/**
 * Change how the client authenticates at the issuer's token endpoint.
 */
export type UpdateRemoteSessionClientFormTokenEndpointAuthMethod = ClosedEnum<
  typeof UpdateRemoteSessionClientFormTokenEndpointAuthMethod
>;

/**
 * Form for updating a remote_session_client. All non-id fields are optional patches. credential_owner is fixed at creation and cannot be changed.
 */
export type UpdateRemoteSessionClientForm = {
  /**
   * Replace the upstream OAuth audience sent for this client. Pass an empty string to clear it. Omit to leave unchanged.
   */
  audience?: string | undefined;
  /**
   * Correct the client_id registered with the issuer. Refused for Client ID Metadata Document (CIMD) clients and clients managed by an identity provider connection. A changed value revokes every remote session obtained through the client, so each user must reconnect. Omit to leave unchanged.
   */
  clientId?: string | undefined;
  /**
   * Rotate the client secret. Speakeasy re-encrypts before persisting.
   */
  clientSecret?: string | undefined;
  /**
   * Replace the client's display name. Pass an empty string to clear it so the client shows its client_id. Omit to leave unchanged.
   */
  displayName?: string | undefined;
  /**
   * The remote_session_client id.
   */
  id: string;
  /**
   * Platform admins only. Set true to run the client in compatibility mode with the legacy callback URL, or false to migrate it to the current callback URL once that URL is registered with the identity provider. Omit to leave unchanged.
   */
  legacyCallbackUrl?: boolean | undefined;
  /**
   * Replace the explicit upstream OAuth scopes for this client. Omit to leave unchanged.
   */
  scope?: Array<string> | undefined;
  /**
   * Change the aud claim format used in private_key_jwt assertions. Omit to leave unchanged.
   */
  tokenEndpointAuthAudienceFormat?:
    | UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat
    | undefined;
  /**
   * Change how the client authenticates at the issuer's token endpoint.
   */
  tokenEndpointAuthMethod?:
    | UpdateRemoteSessionClientFormTokenEndpointAuthMethod
    | undefined;
};

/** @internal */
export const UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat$outboundSchema:
  z.ZodMiniEnum<
    typeof UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat
  > = z.enum(UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat);

/** @internal */
export const UpdateRemoteSessionClientFormTokenEndpointAuthMethod$outboundSchema:
  z.ZodMiniEnum<typeof UpdateRemoteSessionClientFormTokenEndpointAuthMethod> = z
    .enum(UpdateRemoteSessionClientFormTokenEndpointAuthMethod);

/** @internal */
export type UpdateRemoteSessionClientForm$Outbound = {
  audience?: string | undefined;
  client_id?: string | undefined;
  client_secret?: string | undefined;
  display_name?: string | undefined;
  id: string;
  legacy_callback_url?: boolean | undefined;
  scope?: Array<string> | undefined;
  token_endpoint_auth_audience_format?: string | undefined;
  token_endpoint_auth_method?: string | undefined;
};

/** @internal */
export const UpdateRemoteSessionClientForm$outboundSchema: z.ZodMiniType<
  UpdateRemoteSessionClientForm$Outbound,
  UpdateRemoteSessionClientForm
> = z.pipe(
  z.object({
    audience: z.optional(z.string()),
    clientId: z.optional(z.string()),
    clientSecret: z.optional(z.string()),
    displayName: z.optional(z.string()),
    id: z.string(),
    legacyCallbackUrl: z.optional(z.boolean()),
    scope: z.optional(z.array(z.string())),
    tokenEndpointAuthAudienceFormat: z.optional(
      UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat$outboundSchema,
    ),
    tokenEndpointAuthMethod: z.optional(
      UpdateRemoteSessionClientFormTokenEndpointAuthMethod$outboundSchema,
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      clientId: "client_id",
      clientSecret: "client_secret",
      displayName: "display_name",
      legacyCallbackUrl: "legacy_callback_url",
      tokenEndpointAuthAudienceFormat: "token_endpoint_auth_audience_format",
      tokenEndpointAuthMethod: "token_endpoint_auth_method",
    });
  }),
);

export function updateRemoteSessionClientFormToJSON(
  updateRemoteSessionClientForm: UpdateRemoteSessionClientForm,
): string {
  return JSON.stringify(
    UpdateRemoteSessionClientForm$outboundSchema.parse(
      updateRemoteSessionClientForm,
    ),
  );
}
